import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AuthService from "../../../api/services/AuthService";
import { alertErrorMessage, alertSuccessMessage } from "../../../customComponents/CustomAlertMessage";
import { DISABLE_ALL_NETWORK } from "../../../networkConfig";
import "./NotificationSettingsPage.css";

const CHANNEL_VALUES = ["always", "offline_only", "never"];
const DISPLAY_VALUES = ["until_finished", "closed_once", "important_only"];

/** Fallback when GET fails or fields are unknown. */
const DEFAULT_NOTIFICATION_SETTINGS = {
  email_notification: "offline_only",
  short_message: "offline_only",
  notification_display: "closed_once",
};

function pickData(res) {
  const d = res?.data;
  if (d && typeof d === "object" && !Array.isArray(d)) {
    if (d.data && typeof d.data === "object" && !Array.isArray(d.data)) return d.data;
    return d;
  }
  return {};
}

function normalizeSettings(raw) {
  const d = raw && typeof raw === "object" ? raw : {};
  const email = CHANNEL_VALUES.includes(d.email_notification)
    ? d.email_notification
    : DEFAULT_NOTIFICATION_SETTINGS.email_notification;
  const shortMsg = CHANNEL_VALUES.includes(d.short_message)
    ? d.short_message
    : DEFAULT_NOTIFICATION_SETTINGS.short_message;
  const display = DISPLAY_VALUES.includes(d.notification_display)
    ? d.notification_display
    : DEFAULT_NOTIFICATION_SETTINGS.notification_display;
  return {
    email_notification: email,
    short_message: shortMsg,
    notification_display: display,
  };
}

const CHANNEL_OPTIONS = [
  {
    value: "always",
    label: "Notify regardless if I was online or not",
    tag: "(Recommended)",
    tagClass: "notification-settings-page__rec",
  },
  {
    value: "offline_only",
    label: "Only notify when I'm offline",
    tag: null,
    tagClass: "",
  },
  {
    value: "never",
    label: "Do not notify, neither online or offline",
    tag: null,
    tagClass: "",
  },
];

const NOTIFICATION_DISPLAY_OPTIONS = [
  {
    value: "until_finished",
    label: "Display until the task is finished",
    tag: "(Recommended)",
    tagClass: "notification-settings-page__rec",
  },
  {
    value: "closed_once",
    label: "Notices closed by you will not be displayed again",
    tag: null,
    tagClass: "",
  },
  {
    value: "important_only",
    label: "Never display notices of less importance",
    tag: "(Warning: you may miss notices)",
    tagClass: "notification-settings-page__warn-note notification-settings-page__warn-note--emphasis",
  },
];

function RadioGroup({ name, value, onChange, options, disabled = false }) {
  return (
    <div className="notification-settings-page__options" role="radiogroup" aria-label={name}>
      {options.map((opt) => (
        <label key={opt.value} className="notification-settings-page__option">
          <input
            type="radio"
            name={name}
            value={opt.value}
            checked={value === opt.value}
            disabled={disabled}
            onChange={() => onChange(opt.value)}
          />
          <span className="notification-settings-page__option-body">
            {opt.label}
            {opt.tag ? (
              <>
                {" "}
                <span className={opt.tagClass || "notification-settings-page__warn-note"}>{opt.tag}</span>
              </>
            ) : null}
          </span>
        </label>
      ))}
    </div>
  );
}

const NotificationSettingsPage = () => {
  const [settings, setSettings] = useState(() => ({ ...DEFAULT_NOTIFICATION_SETTINGS }));
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (DISABLE_ALL_NETWORK) {
      setSettings({ ...DEFAULT_NOTIFICATION_SETTINGS });
      setHydrated(true);
      return undefined;
    }
    (async () => {
      try {
        const res = await AuthService.getNotificationSettingsSetting();
        if (cancelled) return;
        if (res?.success === false && res?.message) {
          alertErrorMessage(res.message);
        }
        const merged = normalizeSettings(pickData(res));
        setSettings(merged);
      } catch {
        if (!cancelled) setSettings({ ...DEFAULT_NOTIFICATION_SETTINGS });
      } finally {
        if (!cancelled) setHydrated(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleChange = useCallback((field, value) => {
    setSettings((prev) => {
      const next = { ...prev, [field]: value };
      if (DISABLE_ALL_NETWORK) return next;

      void AuthService.updateNotificationSettingsSetting(next).then((res) => {
        if (res?.success === true) {
          alertSuccessMessage(res?.message || "Notification settings saved.");
          return;
        }
        if (res?.success === false) {
          alertErrorMessage(res?.message || "Could not save notification settings.");
        }
      });
      return next;
    });
  }, []);

  return (
    <div className="dashboard_right">
      <div className="notification-settings-page">
        <p className="notification-settings-page__crumb">
          <Link to="/user_profile/profile_setting">Settings</Link>
          <span className="notification-settings-page__crumb-sep">&gt;</span>
          <span>Notification Settings</span>
        </p>
        <h1 className="notification-settings-page__title">Notification Settings</h1>
        {!hydrated && !DISABLE_ALL_NETWORK ? (
          <p className="notification-settings-page__info" aria-live="polite">
            Loading preferences…
          </p>
        ) : null}

        <section className="notification-settings-page__card" aria-labelledby="ns-asset-heading">
          <h2 id="ns-asset-heading" className="notification-settings-page__card-title">
            Asset change notice
          </h2>
          <p className="notification-settings-page__info">
            We will notify you through email or short message when there is a deposit.
          </p>
        </section>

        <section className="notification-settings-page__card" aria-labelledby="ns-email-heading">
          <h2 id="ns-email-heading" className="notification-settings-page__card-title">
            Email notification
          </h2>
          <RadioGroup
            name="email_notification"
            value={settings.email_notification}
            onChange={(v) => handleChange("email_notification", v)}
            options={CHANNEL_OPTIONS}
            disabled={!hydrated}
          />
        </section>

        <section className="notification-settings-page__card" aria-labelledby="ns-short-heading">
          <h2 id="ns-short-heading" className="notification-settings-page__card-title">
            Short message
          </h2>
          <RadioGroup
            name="short_message"
            value={settings.short_message}
            onChange={(v) => handleChange("short_message", v)}
            options={CHANNEL_OPTIONS}
            disabled={!hydrated}
          />
        </section>

        <section className="notification-settings-page__card" aria-labelledby="ns-display-heading">
          <h2 id="ns-display-heading" className="notification-settings-page__card-title">
            Notification
          </h2>
          <RadioGroup
            name="notification_display"
            value={settings.notification_display}
            onChange={(v) => handleChange("notification_display", v)}
            options={NOTIFICATION_DISPLAY_OPTIONS}
            disabled={!hydrated}
          />
        </section>
      </div>
    </div>
  );
};

export default NotificationSettingsPage;
